import React, { useMemo, useState } from 'react';
import { StyleSheet, Text, View } from 'react-native';
import type { NativeStackScreenProps } from '@react-navigation/native-stack';
import { AppButton } from '../../components/AppButton';
import { AppDateField } from '../../components/AppDateField';
import { AppScreen } from '../../components/AppScreen';
import { PermissionGuard } from '../../components/PermissionGuard';
import { ScreenHeader } from '../../components/ScreenHeader';
import { UnauthorizedScreen } from '../system/UnauthorizedScreen';
import { PERMISSIONS } from '../../constants/permissions';
import { routes, type RootStackParamList } from '../../constants/routes';
import { spacing } from '../../constants/spacing';
import { typography } from '../../constants/typography';
import { useAppColors } from '../../context/PreferencesContext';
import {
  createDefaultWfhFilters,
  validateWfhDateRange,
} from '../../utils/wfhFilters';

type Props = NativeStackScreenProps<RootStackParamList, 'WfhFilter'>;

/**
 * History filters supported by GET /wfh/history are fromDate and toDate only.
 * Status, request type, and search are not mounted on the backend.
 */
export function WfhFilterScreen({ navigation, route }: Props) {
  const palette = useAppColors();
  const current = route.params.currentFilters;
  const [fromDate, setFromDate] = useState(current.fromDate);
  const [toDate, setToDate] = useState(current.toDate);
  const [error, setError] = useState<string | null>(null);

  const themed = useMemo(
    () => ({
      help: {
        ...typography.caption,
        color: palette.textMuted,
        marginBottom: spacing.lg,
      },
      error: {
        ...typography.body,
        color: palette.error,
        marginTop: spacing.sm,
      },
    }),
    [palette],
  );

  const apply = () => {
    const rangeError = validateWfhDateRange(fromDate.trim(), toDate.trim());
    if (rangeError) {
      setError(rangeError);
      return;
    }
    navigation.navigate({
      name: routes.wfhRequests,
      params: {
        appliedFilters: {
          fromDate: fromDate.trim(),
          toDate: toDate.trim(),
        },
      },
      merge: true,
    });
  };

  const reset = () => {
    const defaults = createDefaultWfhFilters();
    setFromDate(defaults.fromDate);
    setToDate(defaults.toDate);
    setError(null);
  };

  return (
    <PermissionGuard
      permission={PERMISSIONS.WFH_APPLY}
      fallback={<UnauthorizedScreen />}
    >
      <AppScreen webLayout="narrow" scroll keyboardSafe>
        <ScreenHeader
          title="WFH Filters"
          onBack={navigation.goBack}
        />
        <View style={styles.body}>
        <Text style={themed.help}>
          Select dates from the calendar. Status and request-type filters are not
          available on the current API.
        </Text>
        <AppDateField
          label="From date"
          value={fromDate}
          onChange={value => {
            setFromDate(value);
            setError(null);
          }}
        />
        <AppDateField
          label="To date"
          value={toDate}
          onChange={value => {
            setToDate(value);
            setError(null);
          }}
        />
        {error ? (
          <Text accessibilityRole="alert" style={themed.error}>
            {error}
          </Text>
        ) : null}
        <View style={styles.actions}>
          <AppButton title="Apply" onPress={apply} />
          <AppButton title="Reset" variant="secondary" onPress={reset} />
          <AppButton
            title="Cancel"
            variant="text"
            onPress={() => navigation.goBack()}
          />
        </View>
        </View>
      </AppScreen>
    </PermissionGuard>
  );
}

const styles = StyleSheet.create({
  body: { marginTop: spacing.lg },
  actions: { gap: spacing.sm, marginTop: spacing.xl },
});
